// app/javascript/controllers/address_geocoder_controller.js
import { Controller } from "@hotwired/stimulus"
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

export default class extends Controller {
  static targets = ["address", "latitude", "longitude", "displayAddress", "region", "country"]
  static values = {
    apiKey: String
  }
  
  connect() {
    if (!this.hasAddressTarget) {
      return
    }
    
    if (this.hasApiKeyValue) {
      this.initializeGeocoder()
    } else {
      console.error("Mapbox API key is missing")
    }
  }

  initializeGeocoder() {
    // Créer l'élément de conteneur pour le geocoder
    const geocoderContainer = document.createElement('div')
    geocoderContainer.className = 'mapbox-geocoder-container'
    this.addressTarget.parentNode.insertBefore(geocoderContainer, this.addressTarget)
    
    // Initialiser le geocoder
    this.geocoder = new MapboxGeocoder({
      accessToken: this.apiKeyValue,
      types: 'address,place',
      placeholder: 'Recherchez une adresse',
      language: 'fr',
    })
    
    // Ajouter le geocoder au DOM
    geocoderContainer.appendChild(this.geocoder.onAdd())
    
    // Gérer la sélection d'une adresse
    this.geocoder.on('result', (event) => {
      const result = event.result
      
      // Mettre à jour le champ d'adresse
      this.addressTarget.value = result.place_name
      
      // Mettre à jour les coordonnées
      if (this.hasLatitudeTarget && this.hasLongitudeTarget) {
        const [lng, lat] = result.center
        this.longitudeTarget.value = lng
        this.latitudeTarget.value = lat
      }

      // Mise à jour de la région et du pays
      this.updateLocationInfo(result)
    })
    
    // En cas d'effacement de la recherche
    this.geocoder.on('clear', () => {
      this.addressTarget.value = ''
      if (this.hasLatitudeTarget && this.hasLongitudeTarget) {
        this.latitudeTarget.value = ''
        this.longitudeTarget.value = ''
      }
      if (this.hasRegionTarget) {
        this.regionTarget.value = ''
      }
      if (this.hasCountryTarget) {
        this.countryTarget.value = ''
      }
    })
  }

  updateAddress() {
    if (this.hasDisplayAddressTarget) {
      this.displayAddressTarget.textContent = this.addressTarget.value
      this.displayAddressTarget.classList.remove('hidden')
      this.addressTarget.classList.add('hidden')
    }
    this.geocodeAddress()
  }

  async geocodeAddress() {
    const address = this.addressTarget.value
    if (!address) return

    try {
      const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${this.apiKeyValue}&language=fr`)
      const data = await response.json()

      if (data.features && data.features.length > 0) {
        const feature = data.features[0]
        const [longitude, latitude] = feature.center
        
        this.latitudeTarget.value = latitude
        this.longitudeTarget.value = longitude

        // Mise à jour de la région et du pays
        this.updateLocationInfo(feature)
      }
    } catch (error) {
      console.error("Erreur de géocodage:", error)
    }
  }

  updateLocationInfo(feature) {
    // Vérifier si nous avons la cible region dans le DOM
    if (!this.hasRegionTarget) {
      // Créer un champ caché pour la région si non présent
      const regionInput = document.createElement('input')
      regionInput.type = 'hidden'
      regionInput.name = 'organization[region]'
      regionInput.id = 'organization_region'
      regionInput.setAttribute('data-address-geocoder-target', 'region')
      this.element.appendChild(regionInput)
      this.regionTarget = regionInput
    }

    // Vérifier si nous avons la cible country dans le DOM
    if (!this.hasCountryTarget) {
      // Créer un champ caché pour le pays si non présent
      const countryInput = document.createElement('input')
      countryInput.type = 'hidden'
      countryInput.name = 'organization[country]'
      countryInput.id = 'organization_country'
      countryInput.setAttribute('data-address-geocoder-target', 'country')
      this.element.appendChild(countryInput)
      this.countryTarget = countryInput
    }

    // Extraction des informations de localisation depuis les données de feature
    let regionName = null
    let countryName = null

    // Vérifier dans le contexte de la feature
    if (feature.context) {
      // Chercher une région dans le contexte
      const regionContext = feature.context.find(ctx => 
        ctx.id.includes('region') || 
        ctx.id.includes('district') || 
        ctx.id.includes('locality')
      )
      if (regionContext) {
        regionName = regionContext.text
      }

      // Chercher un pays dans le contexte
      const countryContext = feature.context.find(ctx => ctx.id.includes('country'))
      if (countryContext) {
        countryName = countryContext.text
      }
    }

    // Si pas trouvé dans le contexte, essayer d'autres propriétés pour la région
    if (!regionName) {
      if (feature.place_type && feature.place_type.includes('region')) {
        regionName = feature.text
      } else if (feature.properties && feature.properties.region) {
        regionName = feature.properties.region
      }
    }

    // Si pas trouvé dans le contexte, essayer d'autres propriétés pour le pays
    if (!countryName) {
      if (feature.place_type && feature.place_type.includes('country')) {
        countryName = feature.text
      } else if (feature.properties && feature.properties.country) {
        countryName = feature.properties.country
      }
    }

    // Mettre à jour les valeurs des champs
    if (regionName) {
      this.regionTarget.value = regionName
    }
    
    if (countryName) {
      this.countryTarget.value = countryName
    }
  }
}